import EditorJs from '@editorjs/editorjs';

import { Dispatch } from 'redux';
import hotKeyConverter from '../../../utility/keyCodeConverter';
import CopyHandler from './keyboard/CopyHandler';
import InstructionSeparator from './keyboard/InstructionSeparator';
import KeyUpHandler from './keyboard/KeyUpHandler';
import UndoAndRedoListener from './UndoAndRedoListener';
import { detectMac } from '../../../utility/helpers';

/* eslint-disable lines-between-class-members, @typescript-eslint/no-explicit-any */

type Params = {
  refEditor: HTMLDivElement;
  editor: EditorJs;
  data: any;
  dispatch: Dispatch;
};

class KeyboardEventListener {
  protected refEditor: HTMLDivElement;
  protected editor: EditorJs;
  protected data: any;
  protected dispatch: Dispatch;
  protected keyUpHandler: KeyUpHandler;
  protected isMac: boolean;

  constructor({
    editor, refEditor, data, dispatch,
  }: Params) {
    this.editor = editor;
    this.refEditor = refEditor;
    this.data = data;
    this.dispatch = dispatch;
    this.keyUpHandler = new KeyUpHandler(editor, dispatch);
    this.isMac = detectMac();

    this.init = this.init.bind(this);
    this.destroy = this.destroy.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  init() {
    this.refEditor.addEventListener('keydown', this.onKeyDown);
    this.refEditor.addEventListener('keyup', this.onKeyUp);
  }

  destroy() {
    this.refEditor.removeEventListener('keydown', this.onKeyDown);
    this.refEditor.removeEventListener('keyup', this.onKeyUp);
  }

  protected onKeyDown(e: KeyboardEvent) {
    if (e.ctrlKey || (this.isMac && e.metaKey)) {
      switch (e.keyCode) {
        case hotKeyConverter('c'):
          this.copyHandler(e);
          break;
        case hotKeyConverter('space'):
          this.separateInstruction(e);
          break;

        case hotKeyConverter('z'):
          e.preventDefault();
          e.stopPropagation();
          UndoAndRedoListener.undo(this.editor);
          break;

        case hotKeyConverter('y'):
          e.preventDefault();
          e.stopPropagation();
          UndoAndRedoListener.redo(this.editor);
          break;

        default:
          break;
      }
    } else {
      UndoAndRedoListener.setSelectionToCurrentBlock(this.editor);
      this.keyUpHandler.process();
    }
  }

  protected onKeyUp(e: KeyboardEvent) {
    if (e.keyCode === hotKeyConverter('backspace')) {
      this.keyUpHandler.process();
    }
  }

  protected copyHandler(e: KeyboardEvent) {
    e.preventDefault();
    e.stopPropagation();
    const copy = new CopyHandler({
      editor: this.editor,
      data: this.data,
    });
    copy.process();
  }

  protected separateInstruction(e: KeyboardEvent) {
    e.preventDefault();
    e.stopPropagation();
    const instructionSeparator = new InstructionSeparator({ editor: this.editor });
    instructionSeparator.process();
  }
}

export default KeyboardEventListener;
